import { useEffect, useState } from 'react'
import { Icon } from './Icon'

// ─── Tour steps ───────────────────────────────────────────────────────────────

interface Step {
  icon: string
  iconColor: string
  title: string
  body: string
  action?: string   // CTA label on the last-ish steps
}

const STEPS: Step[] = [
  {
    icon: 'building-2',
    iconColor: '#1A2E4A',
    title: 'Welcome to AutoAxxiz',
    body: 'Your workshop, WhatsApp, and payments — all in one place. This quick tour will show you the key parts of the system.',
  },
  {
    icon: 'clipboard',
    iconColor: '#6366F1',
    title: 'Job cards',
    body: 'Every vehicle visit starts as a job card. Track its status from INTAKE through to PAID. Customers get automatic WhatsApp updates at each stage.',
  },
  {
    icon: 'wa',
    iconColor: '#25D366',
    title: 'WhatsApp inbox',
    body: 'All customer messages land here. Reply directly, or let AutoAxxiz send automated notifications for job updates, quotes, and payment links.',
  },
  {
    icon: 'file-text',
    iconColor: '#0891B2',
    title: 'Quotes & invoices',
    body: 'Create professional quotes with line items. VAT at 15% is calculated automatically. Convert accepted quotes to invoices in one click.',
  },
  {
    icon: 'clipboard-check',
    iconColor: '#D97706',
    title: 'Digital vehicle inspection',
    body: 'Mechanics complete a DVI checklist on their phone — marking items PASS, ADVISORY, or FAIL with photos. Reports attach to the job card automatically.',
  },
  {
    icon: 'layout-grid',
    iconColor: '#7C3AED',
    title: 'Bay board',
    body: 'See every bay at a glance. Drag and drop vehicles between bays, track mechanic assignments, and monitor estimated completion times.',
  },
  {
    icon: 'check-circle',
    iconColor: '#16A34A',
    title: "You're all set!",
    body: "That's the core of AutoAxxiz. Start by creating your first job card — it takes under a minute.",
    action: 'Create first job card',
  },
]

const STORAGE_KEY = 'axxiz_tour_v1'

// ─── Component ────────────────────────────────────────────────────────────────

interface WelcomeTourProps {
  onFinish: () => void
  onCreateJob?: () => void
}

export function WelcomeTour({ onFinish, onCreateJob }: WelcomeTourProps) {
  const [step, setStep] = useState(0)
  const current = STEPS[step]
  const isLast = step === STEPS.length - 1

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, 'done')
    onFinish()
  }

  function next() {
    if (isLast) {
      dismiss()
      onCreateJob?.()
    } else {
      setStep(s => s + 1)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome tour"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, backdropFilter: 'blur(2px)',
        animation: 'fadeIn 150ms ease-out',
      }}
      onClick={e => { if (e.target === e.currentTarget) dismiss() }}
    >
      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 440,
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        animation: 'slideUp 180ms ease-out',
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: '#F3F4F6' }}>
          <div style={{
            height: '100%', background: 'var(--navy-700)',
            width: `${((step + 1) / STEPS.length) * 100}%`,
            transition: 'width 200ms ease-out',
          }} />
        </div>

        {/* Body */}
        <div style={{ padding: '32px 32px 24px' }}>
          {/* Icon */}
          <div style={{
            width: 56, height: 56, borderRadius: 16, marginBottom: 20,
            background: current.iconColor + '15',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name={current.icon} size={26} style={{ color: current.iconColor }} />
          </div>

          {/* Step counter */}
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Step {step + 1} of {STEPS.length}
          </div>

          <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 700, color: 'var(--fg-1)', lineHeight: 1.3 }}>
            {current.title}
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.7 }}>
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 32px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          {/* Dot indicators */}
          <div style={{ display: 'flex', gap: 6 }}>
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                style={{
                  width: i === step ? 18 : 6, height: 6, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 0,
                  background: i === step ? 'var(--navy-700)' : '#D1D5DB',
                  transition: 'width 200ms ease-out, background 150ms',
                }}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={dismiss}
              style={{
                padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border-subtle)',
                background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                color: 'var(--fg-2)', fontFamily: 'inherit',
              }}
            >
              Skip
            </button>
            <button
              onClick={next}
              style={{
                padding: '9px 20px', borderRadius: 8, border: 'none',
                background: isLast ? 'var(--orange-500)' : 'var(--navy-700)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600,
                color: '#fff', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {isLast ? (current.action ?? 'Get started') : 'Next'}
              {!isLast && <Icon name="arrow-right" size={14} />}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </div>
  )
}

// Exported so Help screen can trigger it, and App.tsx can check it
export const TOUR_STORAGE_KEY = STORAGE_KEY

export function shouldShowTour(): boolean {
  return !localStorage.getItem(STORAGE_KEY)
}
